import {
  FeatureFlagDto,
  PermissionAccess,
  ProductLine,
  SubscriptionDto,
} from '@trucktrax/trucktrax-ts-common';
import { store } from '../Main';
import { setFeatureFlagObject } from '../store/actions/featureFlagsActions';
import { RegionAccessMap } from './permissionUtil';
import {
  TRUCKTRAX_GEO_AGGREGATES_FLAG,
  TRUCKTRAX_GEO_READYMIX_FLAG,
  TRUCKTRAX_GEO_FLAG,
} from '../constants/appConstants';

// DEV-3856: legacy flag names. Tenants whose `feature_flags.name` rows have not yet been migrated
// from 'GeoTraxLite*' to 'TruckTrax_Geo*' still expose the old keys, so we fall back to them. This
// decouples the frontend rollout from any per-tenant DB migration. Remove this map (and the
// fallback lookup below) once every tenant DB has been updated.
const LEGACY_FLAG_NAMES: Record<string, string> = {
  [TRUCKTRAX_GEO_FLAG]: 'GeoTraxLite',
  [TRUCKTRAX_GEO_AGGREGATES_FLAG]: 'GeoTraxLiteAggregates',
  [TRUCKTRAX_GEO_READYMIX_FLAG]: 'GeoTraxLiteReadyMix',
};

// Track which region+flag combos have already warned so we only log once per session.
const legacyFallbackWarned = new Set<string>();

interface FlagMap {
  failedCalls: boolean;
  defaultValue: boolean;
  isDeleted: boolean;
  [flagName: string]: SubscriptionDto | boolean;
}

export function mapFeatureFlagsToAssignments(
  featureFlags: FeatureFlagDto[] | null,
  featureFlagAssignments: SubscriptionDto[] | null,
) {
  const featureFlagMap: FlagMap = {
    failedCalls: false,
    defaultValue: false,
    isDeleted: true,
  };

  if (featureFlags == null || featureFlagAssignments == null) {
    featureFlagMap.failedCalls = true;
    return featureFlagMap;
  }

  const arrayToObject = (assignments: SubscriptionDto[]) =>
    assignments.reduce(
      (acc, assignment) => {
        acc[assignment.featureFlag!.url!] = assignment;
        return acc;
      },
      {} as { [flagUrl: string]: SubscriptionDto },
    );

  const assignmentMap = arrayToObject(featureFlagAssignments);

  featureFlags.forEach(featureFlag => {
    featureFlagMap[featureFlag.name!] =
      !!assignmentMap[featureFlag.url!] && !(featureFlag.deleted || featureFlag.archived);
  });

  return featureFlagMap;
}

export const addFlagToStorage = (
  featureFlag: FeatureFlagDto,
  onSuccessCallback: (flags: FeatureFlagDto[]) => void,
) => {
  const { featureFlagObj: featureFlags } = store.getState();
  const flags = featureFlags || [];
  flags.push(featureFlag);
  store.dispatch(setFeatureFlagObject(flags));
  onSuccessCallback(flags);
};

// after successful delete call, remove flag from locateStorage
export const setStoreAndStorage = (flag: FeatureFlagDto) => {
  const { featureFlagObj: featureFlags } = store.getState();
  let flags: FeatureFlagDto[] = featureFlags || [];
  flags = flags.filter(o => o.id !== flag.id);
  store.dispatch(setFeatureFlagObject(flags));
  return flags;
};

interface Region {
  url: string;
}
interface RegionMap {
  name: string;
}

export function sort(items: any) {
  if (items) {
    items.sort((o1: any, o2: any) => {
      const item1 = o1.item.toLowerCase(); // ignore upper and lowercase
      const item2 = o2.item.toLowerCase(); // ignore upper and lowercase
      if (item1 < item2) {
        return -1;
      }
      if (item1 > item2) {
        return 1;
      }

      // names must be equal
      return 0;
    });
  }
  return items;
}

export function getCommaSeparatedRegions(
  regionMap: { [key: string]: RegionMap },
  regions: Region[],
) {
  let commaSeparateName = '';
  if (regionMap && regions) {
    const regionNames = regions
      .map(r => {
        if (regionMap[r.url] && regionMap[r.url].name) {
          return regionMap[r.url].name;
        }
        return null;
      })
      .filter(r => r != null);
    commaSeparateName = `(${regionNames.join(', ')})`;
  }
  return commaSeparateName;
}

export function flattenDataForDisplay(objectToFlatten: { [key: string]: any[] }) {
  let flattenedList: any = [];
  Object.keys(objectToFlatten).forEach(key => {
    flattenedList = flattenedList?.concat(objectToFlatten[key]);
  });
  return flattenedList;
}

/**
 * The flags an access check must pass for a given flag name: the flag itself
 * plus any product-line dependents (currently only TruckTrax_Geo has them).
 */
const targetFlags = (featureFlagName: string): string[] => {
  if (featureFlagName !== TRUCKTRAX_GEO_FLAG) {
    return [featureFlagName];
  }
  const { selectedProductLine } = store.getState();
  if (selectedProductLine === ProductLine.Aggregates) {
    return [featureFlagName, TRUCKTRAX_GEO_AGGREGATES_FLAG];
  }
  if (selectedProductLine === ProductLine.ReadyMix) {
    return [featureFlagName, TRUCKTRAX_GEO_READYMIX_FLAG];
  }
  return [featureFlagName];
};

/**
 * Resolves one flag entry from a region's access map, falling back to the
 * legacy `GeoTraxLite*` name (with a once-per-session warn) while tenant DBs
 * migrate. Both access checks below resolve through here so the dependent-flag
 * expansion and legacy fallback cannot drift between them.
 */
const resolveFlag = (
  regionFeaturesAndPermissions: RegionAccessMap[string],
  regionUrl: string,
  targetFlag: string,
): { access: PermissionAccess } | undefined => {
  const legacyName = LEGACY_FLAG_NAMES[targetFlag];
  const isLegacyFallback =
    legacyName != null &&
    regionFeaturesAndPermissions[targetFlag] == null &&
    regionFeaturesAndPermissions[legacyName] != null;

  if (isLegacyFallback) {
    const warnKey = `${regionUrl}::${targetFlag}`;
    if (!legacyFallbackWarned.has(warnKey)) {
      legacyFallbackWarned.add(warnKey);
      console.warn(
        `[featureFlags] Region "${regionUrl}" still uses legacy flag "${legacyName}" — expected "${targetFlag}". DB migration pending.`,
      );
    }
  }

  return (
    regionFeaturesAndPermissions[targetFlag] ??
    (legacyName ? regionFeaturesAndPermissions[legacyName] : undefined)
  );
};

const everyTargetFlagHasAccess = (
  flagsAndPermissions: RegionAccessMap | null | undefined,
  regionUrl: string,
  featureFlagName: string,
  allowedLevels: PermissionAccess[],
): boolean => {
  const regionFeaturesAndPermissions = flagsAndPermissions?.[regionUrl];
  if (!regionFeaturesAndPermissions) {
    return false;
  }

  return targetFlags(featureFlagName).every(targetFlag => {
    const access = resolveFlag(regionFeaturesAndPermissions, regionUrl, targetFlag)?.access;
    return access != null && allowedLevels.includes(access);
  });
};

/** True when every target flag grants View or Edit — page/feature visibility. */
export const isFeatureFlagEnabled = (
  flagsAndPermissions: RegionAccessMap | null | undefined,
  regionUrl: string,
  featureFlagName: string,
): boolean =>
  everyTargetFlagHasAccess(flagsAndPermissions, regionUrl, featureFlagName, [
    PermissionAccess.View,
    PermissionAccess.Edit,
  ]);

/**
 * DEV-4153: true only when every target flag grants Edit. Right for mutating
 * actions (e.g. acknowledging material receipt) where the View level that
 * makes a page visible must not authorize writes.
 */
export const hasFeatureFlagEditAccess = (
  flagsAndPermissions: RegionAccessMap | null | undefined,
  regionUrl: string,
  featureFlagName: string,
): boolean =>
  everyTargetFlagHasAccess(flagsAndPermissions, regionUrl, featureFlagName, [
    PermissionAccess.Edit,
  ]);
